import React, { useState, useRef, useEffect } from "react";

interface AspectRatioSelectorProps {
  selectedAspectRatio: "16:9" | "9:16";
  onAspectRatioChange: (aspectRatio: "16:9" | "9:16") => void;
  disabled?: boolean;
}

const AspectRatioSelector: React.FC<AspectRatioSelectorProps> = ({
  selectedAspectRatio,
  onAspectRatioChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const aspectRatioOptions = [
    {
      value: "16:9" as const,
      label: "16:9 (Landscape)",
      description: "Widescreen format, perfect for YouTube and desktop viewing",
      icon: "📺",
    },
    {
      value: "9:16" as const,
      label: "9:16 (Portrait)",
      description:
        "Vertical format, ideal for TikTok, Instagram Stories, and mobile",
      icon: "📱",
    },
  ];

  const selectedOption = aspectRatioOptions.find(
    (option) => option.value === selectedAspectRatio
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (aspectRatio: "16:9" | "9:16") => {
    onAspectRatioChange(aspectRatio);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
        Aspect Ratio
      </label>

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          relative w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-left cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700"
              : "hover:border-gray-400 dark:hover:border-gray-500"
          }
          transition-colors duration-200
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-lg">{selectedOption?.icon}</span>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {selectedOption?.label}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedOption?.description}
              </span>
            </div>
          </div>
          <svg
            className={`
              w-5 h-5 text-gray-400 transform transition-transform duration-200
              ${isOpen ? "rotate-180" : ""}
            `}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg transition-colors">
          <div className="py-1">
            {aspectRatioOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`
                  w-full px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700
                  transition-colors duration-150
                  ${
                    selectedAspectRatio === option.value
                      ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                      : "text-gray-900 dark:text-white"
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{option.icon}</span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{option.label}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {option.description}
                    </span>
                  </div>
                  {selectedAspectRatio === option.value && (
                    <div className="ml-auto">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AspectRatioSelector;
